/**
 * TaskBulkEditBar - Barra de edição em massa de tarefas
 * Exibida no topo da listagem quando há tarefas selecionadas
 * 
 * Funcionalidades:
 * - Seletor de Categoria
 * - Seletor de Fase (dependente da categoria)
 * - Seletor de Classificação (dependente da categoria)
 * - Seletor de Prioridade
 * - Seletor de Responsável (CrmUserFilter)
 * - Date picker para Prazo
 * 
 * Versão: 1.0.0
 * RELEASE: 2026-04-09 v1.0.0 - Criação do componente
 */

import { useState, useEffect } from 'react';
import { X, Save, ClipboardList, CircleDot, AlertTriangle, CalendarDays, Tags, User } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { PriorityFlag, PRIORITY_LABELS } from '@/components/tasks/PriorityFlag';
import { CrmUserFilter } from '@/components/crm/filters/CrmUserFilter';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useTaskClassifications } from '@/hooks/useTaskClassifications';
import { useBulkEditTasks } from '@/hooks/useBulkEditTasks';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('TaskBulkEditBar');

// ============================================
// TIPOS
// ============================================

interface TaskBulkEditBarProps {
  /** Quantidade de tarefas selecionadas */
  selectedCount: number;
  /** IDs das tarefas selecionadas */
  selectedTaskIds: string[];
  /** Callback para cancelar/sair do modo edição */
  onCancel: () => void;
  /** Callback após salvar com sucesso */
  onSuccess: () => void;
}

/** Tipo simplificado de fase para o seletor */
interface PhaseOption {
  id: string;
  name: string;
  color: string;
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export function TaskBulkEditBar({
  selectedCount,
  selectedTaskIds,
  onCancel,
  onSuccess,
}: TaskBulkEditBarProps) {
  // Estados de edição
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedPhaseId, setSelectedPhaseId] = useState<string>('');
  const [selectedClassificationId, setSelectedClassificationId] = useState<string>('');
  const [selectedPriority, setSelectedPriority] = useState<string>('');
  const [selectedAssignedTo, setSelectedAssignedTo] = useState<string | null>(null);
  const [selectedDueDate, setSelectedDueDate] = useState<Date | undefined>();
  const [hasDueDateChange, setHasDueDateChange] = useState(false);

  // Fases carregadas dinamicamente
  const [phases, setPhases] = useState<PhaseOption[]>([]);
  const [phasesLoading, setPhasesLoading] = useState(false);

  // Mutation de edição em massa
  const bulkEdit = useBulkEditTasks();

  // Categorias ativas
  const { categories } = useTaskCategories(true);

  // Classificações (dependente da categoria selecionada)
  const { classifications } = useTaskClassifications(selectedCategoryId || undefined, true);

  // ============================================
  // Busca fases ao mudar categoria
  // ============================================
  useEffect(() => {
    if (!selectedCategoryId) {
      setPhases([]);
      return;
    }

    const fetchPhases = async () => {
      setPhasesLoading(true);
      try {
        const { data, error } = await supabase.rpc('get_task_phases', {
          p_category_id: selectedCategoryId,
        });
        if (error) throw error;
        setPhases((data || []) as PhaseOption[]);
      } catch (err: any) {
        logger.error('Erro ao buscar fases', err);
        setPhases([]);
      } finally {
        setPhasesLoading(false);
      }
    };

    fetchPhases();
  }, [selectedCategoryId]);

  // Reset dependentes ao mudar categoria
  useEffect(() => {
    setSelectedPhaseId('');
    setSelectedClassificationId('');
  }, [selectedCategoryId]);

  // ============================================
  // HANDLER DE SUBMIT
  // ============================================
  const handleApply = () => {
    const hasChanges = selectedCategoryId || selectedPhaseId || selectedClassificationId ||
                       selectedPriority || selectedAssignedTo || hasDueDateChange;

    if (!hasChanges) return;

    bulkEdit.mutate(
      {
        taskIds: selectedTaskIds,
        categoryId: selectedCategoryId || undefined,
        phaseId: selectedPhaseId || undefined,
        classificationId: selectedClassificationId || undefined,
        priority: selectedPriority || undefined,
        assignedTo: selectedAssignedTo || undefined,
        dueDate: hasDueDateChange ? (selectedDueDate ? selectedDueDate.toISOString() : null) : undefined,
      },
      {
        onSuccess: () => {
          onSuccess();
        },
      }
    );
  };

  // Verifica se há alterações pendentes
  const hasChanges = !!(selectedCategoryId || selectedPhaseId || selectedClassificationId ||
                        selectedPriority || selectedAssignedTo || hasDueDateChange);

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-primary/5 border-b border-primary/20 animate-in slide-in-from-top-2 duration-200">
      {/* Contador de seleção */}
      <Badge variant="secondary" className="text-xs font-medium">
        {selectedCount} tarefa(s) selecionada(s)
      </Badge>

      <div className="h-4 w-px bg-border" />

      {/* Seletor de Categoria */}
      <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
        <SelectTrigger className="w-[140px] h-7 text-xs">
          <div className="flex items-center gap-1.5">
            <ClipboardList className="h-3 w-3 text-muted-foreground" />
            <span className="truncate">
              {selectedCategoryId
                ? categories.find(c => c.id === selectedCategoryId)?.name
                : 'Categoria'}
            </span>
          </div>
        </SelectTrigger>
        <SelectContent>
          {categories.map(cat => (
            <SelectItem key={cat.id} value={cat.id} className="text-xs">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                {cat.name}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Seletor de Fase (dependente da categoria) */}
      <Select
        value={selectedPhaseId}
        onValueChange={setSelectedPhaseId}
        disabled={!selectedCategoryId || phasesLoading || phases.length === 0}
      >
        <SelectTrigger className={cn(
          "w-[130px] h-7 text-xs",
          !selectedCategoryId && "opacity-50"
        )}>
          <div className="flex items-center gap-1.5">
            <CircleDot className="h-3 w-3 text-muted-foreground" />
            <span className="truncate">
              {selectedPhaseId
                ? phases.find(p => p.id === selectedPhaseId)?.name
                : 'Fase'}
            </span>
          </div>
        </SelectTrigger>
        <SelectContent>
          {phases.map(phase => (
            <SelectItem key={phase.id} value={phase.id} className="text-xs">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: phase.color }} />
                {phase.name}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Seletor de Classificação (dependente da categoria) */}
      <Select
        value={selectedClassificationId}
        onValueChange={setSelectedClassificationId}
        disabled={!selectedCategoryId || classifications.length === 0}
      >
        <SelectTrigger className={cn(
          "w-[140px] h-7 text-xs",
          !selectedCategoryId && "opacity-50"
        )}>
          <div className="flex items-center gap-1.5">
            <Tags className="h-3 w-3 text-muted-foreground" />
            <span className="truncate">
              {selectedClassificationId
                ? classifications.find(c => c.id === selectedClassificationId)?.name
                : 'Classificação'}
            </span>
          </div>
        </SelectTrigger>
        <SelectContent className="max-h-[280px] w-[336px]">
          {classifications.map(cls => (
            <SelectItem key={cls.id} value={cls.id} className="text-xs">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cls.color }} />
                <span className="truncate max-w-[240px] inline-block">{cls.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Seletor de Prioridade */}
      <Select value={selectedPriority} onValueChange={setSelectedPriority}>
        <SelectTrigger className="w-[120px] h-7 text-xs">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="h-3 w-3 text-muted-foreground" />
            <span className="truncate">
              {selectedPriority
                ? PRIORITY_LABELS[selectedPriority as keyof typeof PRIORITY_LABELS]
                : 'Prioridade'}
            </span>
          </div>
        </SelectTrigger>
        <SelectContent>
          {(['urgent', 'high', 'normal', 'low'] as const).map(p => (
            <SelectItem key={p} value={p} className="text-xs">
              <span className="flex items-center gap-2">
                <PriorityFlag priority={p} size={12} />
                {PRIORITY_LABELS[p]}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Seletor de Responsável */}
      <CrmUserFilter
        value={selectedAssignedTo}
        onChange={(id) => setSelectedAssignedTo(id)}
        label="Responsável"
        searchPlaceholder="Buscar responsável..."
        allLabel="Sem alteração"
        groupLabel="Responsáveis"
        className="h-7 text-xs"
      />

      {/* Date picker para Prazo */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-7 px-2.5 text-xs gap-1.5",
              hasDueDateChange && "border-primary text-primary"
            )}
          >
            <CalendarDays className="h-3 w-3" />
            {hasDueDateChange
              ? (selectedDueDate ? format(selectedDueDate, 'dd/MM/yy') : 'Limpar prazo')
              : 'Prazo'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDueDate}
            onSelect={(date) => {
              setSelectedDueDate(date);
              setHasDueDateChange(true);
            }}
            className="p-3 pointer-events-auto"
            locale={ptBR}
          />
          {hasDueDateChange && (
            <div className="px-3 pb-3 flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs flex-1"
                onClick={() => {
                  setSelectedDueDate(undefined);
                  setHasDueDateChange(false);
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs flex-1"
                onClick={() => {
                  setSelectedDueDate(undefined);
                  setHasDueDateChange(true);
                }}
              >
                Limpar prazo
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <div className="flex-1" />

      {/* Botões de Ação */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onCancel}
        className="h-7 text-xs"
      >
        <X className="h-3 w-3 mr-1" />
        Cancelar
      </Button>
      <Button
        size="sm"
        onClick={handleApply}
        disabled={!hasChanges || bulkEdit.isPending}
        className="h-7 text-xs"
      >
        <Save className="h-3 w-3 mr-1" />
        {bulkEdit.isPending ? 'Aplicando...' : 'Aplicar Alterações'}
      </Button>
    </div>
  );
}
